import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";

import {
  Container,
  Stat,
  Row,
  Button,
  MenuWrap,
  MenuButton,
  MenuList,
  MenuItem,
  Dim,
} from "./styles";
import AddUserModal from "../addUserModal";
import InstanceModal from "../instanceModal";
import { useConfirmStore } from "modal/confirm/store";
import { UserPlus, UserMinus, ServerPlus, ServerTrash, MenuIcon } from "./icon";
import { userAPI } from "api";

const ActionPanel = ({ userLength, selectUser }) => {
  const [isAddUserModal, setIsAddUserModal] = useState(false);
  const [isInstanceModalOpen, setInstanceModalOpen] = useState(false);
  const [instanceMode, setInstanceMode] = useState("add");

  const hasSelection = (selectUser?.length ?? 0) > 0;
  const { openConfirm } = useConfirmStore();

  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onGlobalPointerDown = (e: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onGlobalPointerDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onGlobalPointerDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const onAddUser = useCallback(() => {
    setIsAddUserModal(true);
  }, []);

  const onRemoveUser = useCallback(() => {
    if (!hasSelection) return;
    openConfirm({
      title: "유저들 삭제",
      desc: "복구할 수 없습니다. 정말 삭제하시겠습니까?",
      confirmMessage: "삭제하겠습니다",
      onConfirm: async () => {
        for (const user of selectUser) {
          await userAPI.deleteUser(user.name);
        }
      },
    });
  }, [hasSelection, openConfirm, selectUser]);

  const onAddInstance = useCallback(() => {
    if (!hasSelection) return;
    setInstanceMode("add");
    setInstanceModalOpen(true);
  }, [hasSelection]);

  const onDeleteInstanceClick = useCallback(() => {
    if (!hasSelection) return;
    setInstanceMode("delete");
    setInstanceModalOpen(true);
  }, [hasSelection]);

  const actions = useMemo(
    () => [
      {
        key: "addUser",
        label: "Add User",
        variant: "success",
        onClick: onAddUser,
        icon: <UserPlus />,
        disabled: false,
      },
      {
        key: "removeUser",
        label: "Remove User",
        variant: "danger",
        onClick: onRemoveUser,
        icon: <UserMinus />,
        disabled: !hasSelection,
      },
      {
        key: "addInst",
        label: "Add Instance",
        variant: "info",
        onClick: onAddInstance,
        icon: <ServerPlus />,
        disabled: !hasSelection,
      },
      {
        key: "delInst",
        label: "Delete Instance",
        variant: "danger",
        onClick: onDeleteInstanceClick,
        icon: <ServerTrash />,
        disabled: !hasSelection,
      },
    ],
    [
      hasSelection,
      onAddUser,
      onRemoveUser,
      onAddInstance,
      onDeleteInstanceClick,
    ]
  );

  return (
    <>
      <Container>
        <Row>
          <Stat>
            전체: <b>{userLength}</b>개
          </Stat>
          <Stat>
            선택: <b>{selectUser.length}</b>개
          </Stat>
        </Row>

        {/* 데스크탑 */}
        <Row className="btn-row">
          {actions.map((a) => (
            <Button
              key={a.key}
              type="button"
              $variant={a.variant}
              disabled={a.disabled}
              onClick={a.onClick}
            >
              <i>{a.icon}</i>
              {a.label}
            </Button>
          ))}
        </Row>

        {/* 모바일 메뉴 */}
        <MenuWrap ref={menuRef}>
          <MenuButton
            type="button"
            onClick={() => setOpen((v) => !v)}
            title="작업 메뉴"
          >
            <i>
              <MenuIcon />
            </i>
          </MenuButton>

          {open && (
            <>
              <MenuList role="menu">
                {actions.map((a) => (
                  <MenuItem
                    key={a.key}
                    role="menuitem"
                    tabIndex={0}
                    $disabled={a.disabled}
                    onClick={() => {
                      if (a.disabled) return;
                      a.onClick();
                      setOpen(false);
                    }}
                    title={a.title}
                  >
                    <i>{a.icon}</i>
                    {a.label}
                  </MenuItem>
                ))}
              </MenuList>
              <Dim onClick={() => setOpen(false)} />
            </>
          )}
        </MenuWrap>
      </Container>

      <AddUserModal
        isOpen={isAddUserModal}
        onClose={() => setIsAddUserModal(false)}
      />
      <InstanceModal
        isOpen={isInstanceModalOpen}
        onClose={() => setInstanceModalOpen(false)}
        selectUser={selectUser}
        mode={instanceMode}
      />
    </>
  );
};

export default ActionPanel;
